import React, {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {api} from '../api';

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ['DC','District of Columbia'],
];

const CA_PROVINCES = [
  ['AB','Alberta'],['BC','British Columbia'],['MB','Manitoba'],['NB','New Brunswick'],
  ['NL','Newfoundland and Labrador'],['NS','Nova Scotia'],['NT','Northwest Territories'],
  ['NU','Nunavut'],['ON','Ontario'],['PE','Prince Edward Island'],['QC','Quebec'],
  ['SK','Saskatchewan'],['YT','Yukon'],
];

function getCookie(name) {
  const match = document.cookie.split(';').map(c => c.trim()).find(c => c.startsWith(name + '='));
  return match ? match.split('=')[1] : null;
}

export default function Settings() {
  const navigate = useNavigate();
  const tenantName = getCookie('tenantName') ? decodeURIComponent(getCookie('tenantName')) : 'Organization';
  const tenantId = getCookie('tenantId');

  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [subdomain, setSubdomain] = useState('');
  const [subdomainSaved, setSubdomainSaved] = useState('');
  const [subdomainStatus, setSubdomainStatus] = useState(null); // 'available' | 'taken' | 'checking' | null
  const [subdomainSaving, setSubdomainSaving] = useState(false);
  const [biz, setBiz] = useState({legalName:'',dbaName:'',businessAddress:'',businessAddress2:'',businessAddress3:'',businessCity:'',businessState:'',businessZip:'',businessCountry:'US',businessPhone:'',businessEmail:'',businessWebsite:''});
  const [bizSaving, setBizSaving] = useState(false);
  const [bizSaved, setBizSaved] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [serviceClients, setServiceClients] = useState([]);
  const [scName, setScName] = useState('');
  const [scCreating, setScCreating] = useState(false);
  const [scCreated, setScCreated] = useState(null);

  const loadUsers = () => api('/tenant-users').then(r => r.ok ? r.json() : []).then(setUsers);
  const loadInvites = () => api('/invites').then(r => r.ok ? r.json() : []).then(setInvites);
  const loadServiceClients = () => api('/admin/identity/service-clients').then(r => r.ok ? r.json() : []).then(setServiceClients);

  useEffect(() => {
    loadUsers(); loadInvites(); loadServiceClients();
    if (tenantId) {
      api(`/tenants/${tenantId}`).then(r => r.ok ? r.json() : null).then(t => {
        if (t?.subdomain) { setSubdomain(t.subdomain); setSubdomainSaved(t.subdomain); }
        setBiz({legalName:t?.legalName||'',dbaName:t?.dbaName||'',businessAddress:t?.businessAddress||'',businessAddress2:t?.businessAddress2||'',businessAddress3:t?.businessAddress3||'',businessCity:t?.businessCity||'',businessState:t?.businessState||'',businessZip:t?.businessZip||'',businessCountry:t?.businessCountry||'US',businessPhone:t?.businessPhone||'',businessEmail:t?.businessEmail||'',businessWebsite:t?.businessWebsite||''});
        if (t?.logoUrl) setLogoUrl(t.logoUrl);
      });
    }
  }, []);
  useEffect(() => { if (sent) { const t = setTimeout(() => setSent(false), 3000); return () => clearTimeout(t); } }, [sent]);

  const checkSubdomain = async (value) => {
    if (!value || value === subdomainSaved) { setSubdomainStatus(null); return; }
    setSubdomainStatus('checking');
    const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/tenants/by-subdomain/${value}`);
    if (res.status === 404) setSubdomainStatus('available');
    else {
      const data = await res.json();
      setSubdomainStatus(data.id === tenantId ? 'available' : 'taken');
    }
  };

  const handleSubdomainSave = async () => {
    if (!subdomain.trim() || subdomainStatus === 'taken') return;
    setSubdomainSaving(true);
    const res = await api(`/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify({ name: tenantName, subdomain: subdomain.trim().toLowerCase() }),
    });
    if (res.ok) { setSubdomainSaved(subdomain.trim().toLowerCase()); setSubdomainStatus(null); }
    setSubdomainSaving(false);
  };

  const handleSendInvite = (e) => {
    e.preventDefault();
    setSending(true);
    api('/invites', {method: 'POST', body: JSON.stringify({email: inviteEmail})})
      .then(r => { if (r.ok) { setInviteEmail(''); setSent(true); loadInvites(); } })
      .finally(() => setSending(false));
  };

  const handleRevoke = (id) => api(`/invites/${id}`, {method: 'DELETE'}).then(() => loadInvites());
  const handleDeleteInvite = (id) => api(`/invites/${id}`, {method: 'DELETE'}).then(() => loadInvites());

  const statusBadge = (status) => {
    const cls = {PENDING:'bg-yellow-100 text-yellow-800',ACCEPTED:'bg-green-100 text-green-800',EXPIRED:'bg-gray-100 text-gray-800',REVOKED:'bg-red-100 text-red-800'}[status] || 'bg-gray-100 text-gray-800';
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${cls}`}>{status}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-16">
          <div className="flex items-center gap-4">
            <img src="/images/matterking-logo.png" alt="Matter King" className="h-10 w-auto" />
            <h1 className="text-lg font-bold text-gray-900">{tenantName} Settings</h1>
          </div>
          <button onClick={() => navigate('/apps')} className="text-gray-600 hover:text-gray-900 text-sm font-medium">&larr; Back</button>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Employee Portal</h2>
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <label className="block text-sm font-medium text-gray-700 mb-2">Portal Subdomain</label>
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center">
              <input type="text" value={subdomain} onChange={e => { setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setSubdomainStatus(null); }}
                onBlur={e => checkSubdomain(e.target.value)}
                placeholder="your-company"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
              <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-500 text-sm">.hcmcentral.com</span>
            </div>
            <button onClick={handleSubdomainSave} disabled={subdomainSaving || subdomainStatus === 'taken' || !subdomain.trim() || subdomain === subdomainSaved}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm">
              {subdomainSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
          {subdomainStatus === 'checking' && <p className="text-sm text-gray-500 mt-1">Checking availability...</p>}
          {subdomainStatus === 'available' && <p className="text-sm text-green-600 mt-1">✓ Available</p>}
          {subdomainStatus === 'taken' && <p className="text-sm text-red-600 mt-1">✕ Already taken</p>}
          {subdomainSaved && <p className="text-sm text-gray-500 mt-2">Your portal: <a href={`https://${subdomainSaved}.hcmcentral.com`} target="_blank" rel="noreferrer" className="text-red-600 hover:text-red-800">{subdomainSaved}.hcmcentral.com</a></p>}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Business Information</h2>
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          {/* Logo */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
            <div className="flex items-center gap-4">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="h-16 w-auto rounded border" />
              ) : (
                <div className="h-16 w-16 bg-gray-100 rounded border flex items-center justify-center text-gray-400 text-xs">No logo</div>
              )}
              <div className="flex gap-2">
                <label className={`px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer ${logoUploading ? 'opacity-50 pointer-events-none' : ''}`}>
                  {logoUploading ? 'Uploading...' : 'Upload'}
                  <input type="file" accept="image/*" className="hidden" onChange={async e => {
                    const file = e.target.files[0]; if (!file) return;
                    setLogoUploading(true);
                    const formData = new FormData(); formData.append('file', file);
                    const jwtToken = getCookie('jwtToken'); const headers = {};
                    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
                    const tid = getCookie('tenantId'); if (tid) headers['X-Tenant-Id'] = tid;
                    const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/tenants/${tenantId}/logo`, {method: 'POST', headers, body: formData});
                    if (res.ok) { const t = await res.json(); if (t.logoUrl) setLogoUrl(t.logoUrl); }
                    setLogoUploading(false); e.target.value = '';
                  }} />
                </label>
                {logoUrl && <button onClick={async () => {
                  await api(`/tenants/${tenantId}/logo`, {method: 'DELETE'});
                  setLogoUrl(null);
                }} className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50">Remove</button>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Legal Name</label>
              <input type="text" value={biz.legalName} onChange={e => setBiz({...biz, legalName: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Doing Business As</label>
              <input type="text" value={biz.dbaName} onChange={e => setBiz({...biz, dbaName: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address 1</label>
              <input type="text" value={biz.businessAddress} onChange={e => setBiz({...biz, businessAddress: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address 2</label>
              <input type="text" value={biz.businessAddress2} onChange={e => setBiz({...biz, businessAddress2: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Address 3</label>
              <input type="text" value={biz.businessAddress3} onChange={e => setBiz({...biz, businessAddress3: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input type="text" value={biz.businessCity} onChange={e => setBiz({...biz, businessCity: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State/Province</label>
              <select value={biz.businessState} onChange={e => setBiz({...biz, businessState: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500">
                <option value="">Select...</option>
                {(biz.businessCountry === 'CA' ? CA_PROVINCES : US_STATES).map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
              <input type="text" value={biz.businessZip} onChange={e => setBiz({...biz, businessZip: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select value={biz.businessCountry} onChange={e => setBiz({...biz, businessCountry: e.target.value, businessState: ''})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500">
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input type="tel" value={biz.businessPhone} onChange={e => setBiz({...biz, businessPhone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input type="email" value={biz.businessEmail} onChange={e => setBiz({...biz, businessEmail: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
              <input type="url" value={biz.businessWebsite} onChange={e => setBiz({...biz, businessWebsite: e.target.value})} placeholder="https://"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <button onClick={async () => {
              setBizSaving(true);
              await api(`/tenants/${tenantId}`, {method: 'PUT', body: JSON.stringify({name: tenantName, subdomain: subdomainSaved || undefined, ...biz})});
              setBizSaving(false); setBizSaved(true); setTimeout(() => setBizSaved(false), 3000);
            }} disabled={bizSaving} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm">
              {bizSaving ? 'Saving...' : 'Save Business Info'}
            </button>
            {bizSaved && <span className="text-sm text-green-600">✓ Saved</span>}
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">API Keys</h2>
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <form onSubmit={async e => {
            e.preventDefault(); if (!scName.trim()) return;
            setScCreating(true); setScCreated(null);
            const res = await api('/admin/identity/service-clients', {method: 'POST', body: JSON.stringify({name: scName.trim()})});
            if (res.ok) { const data = await res.json(); setScCreated(data); setScName(''); loadServiceClients(); }
            setScCreating(false);
          }} className="flex flex-col sm:flex-row gap-3 mb-4">
            <input type="text" required placeholder="API key name" value={scName} onChange={e => setScName(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            <button type="submit" disabled={scCreating} className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
              {scCreating ? 'Creating...' : 'Create API Key'}
            </button>
          </form>
          {scCreated && (
            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm font-medium text-yellow-800 mb-2">⚠ Copy these credentials now — the secret will not be shown again.</p>
              <div className="text-sm space-y-1">
                <div><span className="font-medium text-gray-700">Client ID:</span> <code className="bg-gray-100 px-1 rounded">{scCreated.clientId}</code></div>
                <div><span className="font-medium text-gray-700">Client Secret:</span> <code className="bg-gray-100 px-1 rounded">{scCreated.clientSecret}</code></div>
              </div>
            </div>
          )}
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-gray-500">
                <th className="p-4">Name</th>
                <th className="p-4 hidden sm:table-cell">Client ID</th>
                <th className="p-4 hidden sm:table-cell">Created</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {serviceClients.map(sc => (
                <tr key={sc.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">{sc.name}</td>
                  <td className="p-4 text-gray-500 text-sm hidden sm:table-cell"><code>{sc.clientId}</code></td>
                  <td className="p-4 text-gray-500 text-sm hidden sm:table-cell">{sc.dateCreated ? new Date(sc.dateCreated).toLocaleDateString() : ''}</td>
                  <td className="p-4 text-right">
                    <button onClick={async () => { await api(`/admin/identity/service-clients/${sc.id}`, {method: 'DELETE'}); setServiceClients(prev => prev.filter(s => s.id !== sc.id)); }}
                      className="text-sm text-red-600 hover:text-red-800">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {serviceClients.length === 0 && <div className="text-center py-8 text-gray-500">No API keys created yet.</div>}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Users</h2>
        <div className="bg-white rounded-lg shadow mb-8">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-gray-500">
                <th className="p-4">Name</th>
                <th className="p-4 hidden sm:table-cell">Email</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.userId} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="p-4 font-medium text-gray-900">{u.firstName} {u.lastName}</td>
                  <td className="p-4 text-gray-500 hidden sm:table-cell">{u.username}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <div className="text-center py-8 text-gray-500">No users found.</div>}
        </div>

        <h2 className="text-2xl font-bold text-gray-900 mb-6">Invite Users</h2>
        {sent && <div className="mb-4 px-4 py-3 bg-green-100 text-green-800 rounded-lg">Invite sent successfully.</div>}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <form onSubmit={handleSendInvite} className="flex flex-col sm:flex-row gap-3">
            <input type="email" required placeholder="Email address" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500" />
            <button type="submit" disabled={sending} className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
              {sending ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
        </div>
        <div className="bg-white rounded-lg shadow">
          <table className="w-full">
            <thead>
              <tr className="border-b text-left text-sm text-gray-500">
                <th className="p-4">Email</th>
                <th className="p-4">Status</th>
                <th className="p-4 hidden sm:table-cell">Expires</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {invites.map(inv => (
                <tr key={inv.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="p-4 text-gray-900">{inv.email}</td>
                  <td className="p-4">{statusBadge(inv.status)}</td>
                  <td className="p-4 text-gray-500 text-sm hidden sm:table-cell">{new Date(inv.expiresAt).toLocaleDateString()}</td>
                  <td className="p-4 text-right">
                    {inv.status === 'PENDING' && <button onClick={() => handleRevoke(inv.id)} className="text-sm text-red-600 hover:text-red-800">Revoke</button>}
                    {inv.status === 'REVOKED' && <button onClick={() => handleDeleteInvite(inv.id)} className="text-sm text-red-600 hover:text-red-800">Delete</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {invites.length === 0 && <div className="text-center py-8 text-gray-500">No invites sent yet.</div>}
        </div>
      </main>
    </div>
  );
}
